import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import { fetchDFRPrices } from "../../../../../api/services/dfr";

const INVOICE_AREA_LIMIT = 1000;

const CHECKOUT_STATES = {
  IDLE: "idle",
  LOADING: "loading",
  SUCCESS: "success",
  ERROR: "error",
  LARGE_PROPERTY: "large_property",
};

export const useCheckoutData = (initialData = {}) => {
  const [checkoutData, setCheckoutData] = useState(() => {
    const stored = Cookies.get("plus_order_data");
    return stored ? JSON.parse(stored) : initialData;
  });

  const [state, setState] = useState(CHECKOUT_STATES.IDLE);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (checkoutData) {
      Cookies.set("plus_order_data", JSON.stringify(checkoutData));
    }
  }, [checkoutData]);

  const updateCheckoutData = (updates) => {
    setCheckoutData((prev) => ({ ...prev, ...updates }));
  };

  const clearCheckoutData = () => {
    Cookies.remove("plus_order_data");
    setCheckoutData({});
    setState(CHECKOUT_STATES.IDLE);
    setError(null);
  };

  const fetchPrices = async (property, couponCode = null) => {
    if (!property.ref) return;

    setState(CHECKOUT_STATES.LOADING);
    setError(null);

    const { data, status, error, message } = await fetchDFRPrices({
      ref: property.ref,
      coupon_code: couponCode,
    });

    if (error) {
      setState(CHECKOUT_STATES.ERROR);
      if (status === 400 && message !== "Property not found on user") {
        setError("Ogiltlig rabattkod. Töm rabattkodsfältet och försök igen.");
      } else {
        setError("Gick inte att hämta pris för fastighet.");
      }

      updateCheckoutData({
        couponCode: null,
        couponDiscountPrice: null,
        couponDiscountPercentage: null,
        basePrice: null,
        netPrice: null,
        tax: null,
        propertyDiscountPrice: null,
        propertyDiscountPercentage: null,
      });
      return;
    }

    const area = data?.area || property?.area || 0;

    if (area > INVOICE_AREA_LIMIT) {
      setState(CHECKOUT_STATES.LARGE_PROPERTY);
      updateCheckoutData({
        property: {
          ref: property.ref,
          area,
          designation: property?.designation,
        },
      });
      return;
    }

    setState(CHECKOUT_STATES.SUCCESS);

    updateCheckoutData({
      property: {
        ref: property.ref,
        area,
        designation: property?.designation,
      },
      basePrice: data.base_price,
      couponDiscountPrice: data.coupon_discount_price,
      couponDiscountPercentage: data.coupon_discount_percentage,
      tax: data.tax,
      netPrice: data.net_price,
      propertyDiscountPrice: data.property_discount_price,
      propertyDiscountPercentage: data.property_discount_percentage,
      couponCode,
    });
  };

  const isLoading = state === CHECKOUT_STATES.LOADING;
  const isSuccess = state === CHECKOUT_STATES.SUCCESS;
  const isError = state === CHECKOUT_STATES.ERROR;
  const isLargeProperty = state === CHECKOUT_STATES.LARGE_PROPERTY;
  const hasValidData = isSuccess && checkoutData.basePrice;
  const canProceedToPayment = hasValidData || isLargeProperty;

  return {
    checkoutData,
    updateCheckoutData,
    clearCheckoutData,
    fetchPrices,
    error,

    // State flags
    isLoading,
    isError,
    isLargeProperty,

    // Business logic
    hasValidData,
    canProceedToPayment,

    // Prices
    netPrice: checkoutData?.netPrice,
    basePrice: checkoutData?.basePrice,
  };
};
